import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import { getCustomVerifierStatus } from "../api/get-test-status";
import { testCustomVerifier } from "../api/test-custom-verifier";

const POLL_INTERVAL_MS = 1000;

export type TestStatus =
  | "idle"
  | "starting"
  | "pending"
  | "completed"
  | "failed";

type Options = {
  verifierUrl?: string | null;
  userId: string;
};

export const useCustomVerifierTest = ({ verifierUrl, userId }: Options) => {
  const scope = useResourceScope();
  const client = getEngineApi(scope);

  const mutation = useMutation({
    mutationFn: () =>
      testCustomVerifier({
        client,
        payload: {
          user_id: userId.trim(),
          verifier_url: (verifierUrl ?? "").trim(),
        },
      }),
    onSuccess: (data) => {
      window.open(data.next_uri, "_blank", "noopener,noreferrer");
    },
    onError: () => {
      toast.error("Custom verifier test failed.");
    },
  });

  const flowId = mutation.data?.flow_id;

  const query = useQuery({
    queryKey: ["custom-verifier-test", flowId],
    queryFn: ({ signal }) => {
      if (!flowId) {
        throw new Error("Missing verifier flow id");
      }
      return getCustomVerifierStatus({
        client,
        params: { id: flowId, wait: 30, signal },
      });
    },
    enabled: mutation.isSuccess && Boolean(flowId),
    retry: false,
    refetchInterval: (q) =>
      q.state.status === "error" ||
      q.state.data?.status === "completed" ||
      q.state.data?.status === "failed"
        ? false
        : POLL_INTERVAL_MS,
  });

  const cancel = () => {
    mutation.reset();
    toast("Test cancelled");
  };

  const start = () => {
    if (!(userId.trim() && verifierUrl)) {
      toast.error("User ID and verifier URL are required.");
      return;
    }
    // Ensure previous attempt state (data/error) does not leak into a restart.
    // In particular, we don't want the previous `flowId` to keep the query in an error state
    // while a new mutation is starting.
    mutation.reset();
    mutation.mutate();
  };

  const getStatus = (): TestStatus => {
    if (mutation.isPending) {
      return "starting";
    }
    if (mutation.isError || query.isError) {
      return "failed";
    }
    if (query.data) {
      return query.data.status;
    }
    if (mutation.isSuccess) {
      return "pending";
    }
    return "idle";
  };
  const status = getStatus();

  return {
    start,
    cancel,
    status,
    nextUri: mutation.data?.next_uri,
    canStart: Boolean(verifierUrl && userId.trim()) && !mutation.isPending,
  };
};
